// Numbers, money, percentages, dates and durations in the portal's language.
// Framework-free (unit-tested without mounting anything).
//
// The one place these formats live. New code formats through here instead of
// toLocaleString('en-GB') / `€${n}` / toFixed — those stay English whatever the
// user picked. Formats follow docs/reference/dutch_glossary.md §2:
//
//                 English              Dutch
//   number        1,234.5              1.234,5
//   euro          €1,234               € 1.234
//   percent       12.5%                12,5%
//   points        +16pp                +16 %-punt
//   date numeric  17/10/2026           17/10/2026   (dd/mm/yyyy in both)
//   date short    17 Oct               17 okt
//   date long     Saturday 17 October 2026   zaterdag 17 oktober 2026
//   time          15:00                15:00
//   duration      48 sec · 1 min 10 sec · 1 h 5 min   … · 1 uur 5 min
//
// Every function takes an optional `lang`; without it the current portal
// language is used.

import { currentLanguage, normalizeLanguage, DEFAULT_LANGUAGE } from './index.js'

// Intl locale per portal language. Plain 'nl' (not nl-NL / nl-BE): it gives the
// shared Dutch formats, and the two regional variants are identical for these.
const INTL_LOCALE = { en: 'en-GB', nl: 'nl' }

const langOf = (lang) => normalizeLanguage(lang) || normalizeLanguage(currentLanguage()) || DEFAULT_LANGUAGE
const localeOf = (lang) => INTL_LOCALE[langOf(lang)]
const isNum = (n) => n !== null && n !== undefined && n !== '' && Number.isFinite(Number(n))
const DASH = '—'

export function formatNumber(n, { decimals = 0, lang } = {}) {
  if (!isNum(n)) return DASH
  return new Intl.NumberFormat(localeOf(lang), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number(n))
}

export function formatEuro(n, { decimals = 0, lang } = {}) {
  if (!isNum(n)) return DASH
  return new Intl.NumberFormat(localeOf(lang), {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number(n))
}

// `value` is already a percentage: 12.5 -> "12.5%" / "12,5%".
export function formatPercent(value, { decimals = 0, signed = false, lang } = {}) {
  if (!isNum(value)) return DASH
  return new Intl.NumberFormat(localeOf(lang), {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    signDisplay: signed ? 'exceptZero' : 'auto',
  }).format(Number(value) / 100)
}

// Percentage points (a difference between two percentages): "+16pp" / "+16 %-punt".
export function formatPercentPoints(delta, { decimals = 0, signed = true, lang } = {}) {
  if (!isNum(delta)) return DASH
  const num = new Intl.NumberFormat(localeOf(lang), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    signDisplay: signed ? 'exceptZero' : 'auto',
  }).format(Number(delta))
  return langOf(lang) === 'nl' ? `${num} %-punt` : `${num}pp`
}

// 'YYYY-MM-DD' is a calendar day, not a UTC instant: read it as a local date so
// it never shifts a day in a timezone west of UTC.
function toDate(d) {
  if (d instanceof Date) return Number.isNaN(d.getTime()) ? null : d
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split('-').map(Number)
    return new Date(y, m - 1, day)
  }
  if (d === null || d === undefined || d === '') return null
  const parsed = new Date(d)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const pad2 = (n) => String(n).padStart(2, '0')

// style: 'numeric' 17/10/2026 · 'short' 17 Oct · 'medium' 17 Oct 2026 ·
//        'long' Saturday 17 October 2026
export function formatDate(d, { style = 'medium', lang } = {}) {
  const date = toDate(d)
  if (!date) return DASH
  if (style === 'numeric') {
    return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`
  }
  const loc = localeOf(lang)
  if (style === 'short') {
    return new Intl.DateTimeFormat(loc, { day: 'numeric', month: 'short' }).format(date)
  }
  const dayMonthYear = new Intl.DateTimeFormat(loc, {
    day: 'numeric', month: style === 'long' ? 'long' : 'short', year: 'numeric',
  }).format(date)
  if (style === 'long') {
    // Built from two parts so English doesn't get Intl's "Saturday, 17 …" comma.
    return `${formatWeekday(date, { lang })} ${dayMonthYear}`
  }
  return dayMonthYear
}

// "Saturday" / "zaterdag" (short: "Sat" / "za").
export function formatWeekday(d, { short = false, lang } = {}) {
  const date = toDate(d)
  if (!date) return DASH
  return new Intl.DateTimeFormat(localeOf(lang), { weekday: short ? 'short' : 'long' }).format(date)
}

// 24-hour "15:00" in both languages.
export function formatTime(d) {
  const date = toDate(d)
  if (!date) return DASH
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`
}

// Seconds as people say them: "48 sec", "2 min", "1 min 10 sec", "1 h 5 min"
// ("1 uur 5 min"). Never decimal minutes. Seconds are dropped from an hour or more.
export function formatDuration(seconds, { lang } = {}) {
  if (!isNum(seconds)) return DASH
  const total = Math.max(0, Math.round(Number(seconds)))
  const hourWord = langOf(lang) === 'nl' ? 'uur' : 'h'
  if (total < 60) return `${total} sec`
  if (total < 3600) {
    const m = Math.floor(total / 60)
    const s = total % 60
    return s ? `${m} min ${s} sec` : `${m} min`
  }
  const h = Math.floor(total / 3600)
  const m = Math.round((total % 3600) / 60)
  if (m === 60) return `${h + 1} ${hourWord}`
  return m ? `${h} ${hourWord} ${m} min` : `${h} ${hourWord}`
}
